import { ReadOnlyWrapper } from "components/common/ReadOnlyWrapper";
import Checkbox from "components/UI/Checkbox";
import FlexboxContainerComponent from "components/UI/FlexboxContainer";
import RadioList from "components/UI/RadioList";
import FeatureFlag from "data/FeatureFlag";
import React, { FunctionComponent, useEffect, useState } from "react";
import styled from "styled-components";
import { Main, Text } from "styles/themeColors";
import { Visibility, VisibilityOff, FileCopy } from '@mui/icons-material';
import { toast } from 'components/UI/ToastNotification';
import Input from 'components/UI/Input';
import { useAPI } from 'lib/API';
import { getBuyNowConfiguration } from 'requests/vendor';
import BuyNowConfigModal from 'components/Brand/BuyNowConfigModal';
import { API_LEVEL } from "constants/buyNow";

const CheckboxContainer = styled.div`
    margin-top: 20px;
`

const Description = styled.p<{ disabled?: boolean }>`
    margin: 0;
    color: ${({ disabled }) => (disabled ? Text.disabled : Text.primary)};
    font-size: 14px;
`

const ConfigureButton = styled.div`
  font-size: 14px;
  color: ${Main.brandDark};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const FieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ActionButtonsContainer = styled.div`
  position: absolute;
  right: 10px;
  top: 72.5%;
  transform: translateY(-50%);
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconButton = styled.div`
  cursor: pointer;
  color: ${Main.brandDark};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  svg {
    font-size: 18px;
  }
`;

interface FieldActionButtonsProps {
    isVisible: boolean;
    onToggleVisibility: () => void;
    onCopy: () => void;
}

const FieldActionButtons: React.FC<FieldActionButtonsProps> = ({ isVisible, onToggleVisibility, onCopy }) => (
    <ActionButtonsContainer>
        <IconButton
            onClick={onToggleVisibility}
            title={isVisible ? 'Hide' : 'Show'}>
            {isVisible ? <VisibilityOff /> : <Visibility />}
        </IconButton>
        <IconButton onClick={onCopy} title="Copy to clipboard">
            <FileCopy />
        </IconButton>
    </ActionButtonsContainer>
);

export const VENDOR_OPTIONS = {
    SLB_VENDOR: 'SLB_VENDOR',
    NON_SLB_VENDOR: 'NON_SLB_VENDOR',
};

export const VENDOR_OPTIONS_LABELS = {
    [VENDOR_OPTIONS.SLB_VENDOR]: 'SLB Vendors',
    [VENDOR_OPTIONS.NON_SLB_VENDOR]: 'Non-SLB Vendors',
};

interface AutomationSettingsProps {
    disabled: boolean;
    vendor: any;
    setVendor: (vendor: any) => void;
    featureFlags: FeatureFlag;
    readOnly?: boolean;
    vendorId: number;
}

const AutomationSettings: FunctionComponent<AutomationSettingsProps> = (props) => {
    const { disabled, vendor, setVendor, readOnly, vendorId } = props;
    const [selectedVendorOption, setSelectedVendorOption] = useState(vendor.isSlbVendor ? VENDOR_OPTIONS.SLB_VENDOR : VENDOR_OPTIONS.NON_SLB_VENDOR);
    const [buyNowCredentials, setBuyNowCredentials] = useState<{
        clientId: string;
        clientSecret: string;
        clientUrl: string;
        buyNowConsumerKey: string;
        vendorCode: string;
    } | null>(null);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [showClientId, setShowClientId] = useState(false);
    const [showClientSecret, setShowClientSecret] = useState(false);
    const [showClientUrl, setShowClientUrl] = useState(false);
    const [showVendorKey, setShowVendorKey] = useState(false);

    // API hook for fetching Buy Now configuration
    const buyNowConfigReq = useAPI({
        deferFn: getBuyNowConfiguration,
        onResolve: (result: any) => {
            if (result) {
                setBuyNowCredentials({
                    clientId: result.clientId || '',
                    clientSecret: result.clientSecret || '',
                    clientUrl: result.clientUrl || '',
                    buyNowConsumerKey: result.buyNowConsumerKey || '',
                    vendorCode: result.vendorCode || '',
                });

                setVendor(
                    {
                        ...vendor,
                        configuration: {
                            ...vendor.configuration,
                            consumerKey: result.buyNowConsumerKey || ''
                        }
                    }
                );
            }
        },
        onReject: (err: any) => {
            console.error('Failed to fetch Buy Now configuration:', err);
        },
    });

    // Update the vendor object when the selected option changes
    useEffect(() => {
        setVendor({ ...vendor, isSlbVendor: selectedVendorOption === VENDOR_OPTIONS.SLB_VENDOR });
    }, [selectedVendorOption]);

    // Reset buyNowCredentials when vendor changes
    useEffect(() => {
        setBuyNowCredentials(null);
    }, [vendor.code]);

    useEffect(() => {
        if (vendor.configuration?.isBuyNowEnabled && !buyNowCredentials && vendor.code) {
            buyNowConfigReq.run(vendor.code);
        }
    }, [vendor.configuration?.isBuyNowEnabled, vendor.code, buyNowCredentials]);

    const onChangeMaterialApi = (isMaterialApiEnabled: boolean) => {
        setVendor({
            ...vendor,
            isMaterialApiEnabled: isMaterialApiEnabled,
            isStockApiEnabled: false,
            isSalesApiEnabled: false,
            configuration: {
                ...vendor.configuration
            }
        })
    }

    const onChangeStockApi = (isStockApiEnabled: boolean) => {
        setVendor({ ...vendor, isStockApiEnabled, isSalesApiEnabled: isStockApiEnabled ? vendor.isSalesApiEnabled : false });
    };

    const onChangeSalesApi = (isSalesApiEnabled: boolean) => {
        setVendor({ ...vendor, isSalesApiEnabled });
    };

    const onChangeBuyNowApi = (isBuyNowEnabled: boolean) => {
        setVendor({
            ...vendor,
            configuration: {
                ...vendor.configuration,
                isBuyNowEnabled
            }
        });

        if (!isBuyNowEnabled) {
            setBuyNowCredentials(null);
            setShowClientId(false);
            setShowClientSecret(false);
            setShowClientUrl(false);
            setShowVendorKey(false);
        }
    };

    const copyToClipboard = (text: string, fieldName: string) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success(`${fieldName} copied to clipboard!`);
        }).catch(() => {
            toast.error('Failed to copy to clipboard');
        });
    };

    const getMaskedValue = (value: string) => {
        return '•'.repeat(value.length);
    };

    return (
        <FlexboxContainerComponent flexDirection="column">
            <ReadOnlyWrapper readOnly={readOnly}>
                <RadioList
                    isDisabled={disabled}
                    options={VENDOR_OPTIONS_LABELS}
                    selectedOption={selectedVendorOption}
                    setSelectedOption={setSelectedVendorOption}
                    color={Main.brandLight}
                />
            </ReadOnlyWrapper>
            <CheckboxContainer>
                <ReadOnlyWrapper readOnly={readOnly}>
                    <Checkbox
                        id="material-api-check"
                        name="isMaterialApiEnabled"
                        disabled={disabled}
                        label="Material API"
                        checked={vendor.isMaterialApiEnabled}
                        appearance="none"
                        checkboxSize="s"
                        onChange={onChangeMaterialApi}
                        theme={'brandLight'}
                        bgColor={Main.brandLight}
                    />
                </ReadOnlyWrapper>

                <Description>
                    This API helps you stay organized by tracking all the products used by your company via unique identifier, SKU number. However, if you prefer, you can still revert to the manual process by deselecting the same checkbox.
                </Description>

            </CheckboxContainer>

            {
                (props.featureFlags.stocks && props.featureFlags.stocks.enabled) ?
                    <CheckboxContainer>
                        <ReadOnlyWrapper readOnly={readOnly}>
                            <Checkbox
                                id="Stock-api-check"
                                name="isStockApiEnabled"
                                disabled={!vendor.isMaterialApiEnabled || disabled}
                                label="Stock API"
                                checked={vendor.isStockApiEnabled}
                                appearance="none"
                                checkboxSize="s"
                                onChange={onChangeStockApi}
                                theme={'brandLight'}
                                bgColor={Main.brandLight}
                            />
                        </ReadOnlyWrapper>

                        <Description disabled={!vendor.isMaterialApiEnabled}>
                            This API helps you automate your inventory management. However, if you prefer, you can still revert to the manual process by deselecting the same checkbox.
                        </Description>
                    </CheckboxContainer> : null
            }

            {
                (props.featureFlags.salesOrder && props.featureFlags.salesOrder.enabled) &&
                <CheckboxContainer>
                    <ReadOnlyWrapper readOnly={readOnly}>
                        <Checkbox
                            id="Sales-api-check"
                            name="isSalesApiEnabled"
                            disabled={!vendor.isMaterialApiEnabled || !vendor.isStockApiEnabled || disabled}
                            label="Sales Order API"
                            checked={vendor.isSalesApiEnabled}
                            appearance="none"
                            checkboxSize="s"
                            onChange={onChangeSalesApi}
                            theme={'brandLight'}
                            bgColor={Main.brandLight}
                        />
                    </ReadOnlyWrapper>

                    <Description disabled={!vendor.isMaterialApiEnabled || !vendor.isStockApiEnabled}>
                        This API helps you automate sales order creation. Requires both Material API and Stock API to be enabled first. However, if you prefer, you can still revert to the manual process by deselecting the same checkbox.
                    </Description>
                </CheckboxContainer>
            }

            {
                vendorId &&
                <>
                    <CheckboxContainer>
                        <ReadOnlyWrapper readOnly={readOnly}>
                            <Checkbox
                                id="buy-now-api-check"
                                name="isBuyNowEnabled"
                                disabled={disabled}
                                label="Buy Now API"
                                checked={vendor.configuration?.isBuyNowEnabled || false}
                                appearance="none"
                                checkboxSize="s"
                                onChange={onChangeBuyNowApi}
                                theme={'brandLight'}
                                bgColor={Main.brandLight}
                            />
                        </ReadOnlyWrapper>

                        <Description>
                            Enable a Buy Now button on your own website for products listed on OFMP. When customers click the button, they'll be redirected to OFMP to complete their purchase through our secure checkout.
                            <ConfigureButton onClick={() => setShowConfigModal(true)}>
                                How to Configure
                            </ConfigureButton>
                        </Description>
                        {vendor.configuration?.isBuyNowEnabled && buyNowCredentials && (
                            <>
                                <div style={{ marginTop: '20px' }}>
                                    <FlexboxContainerComponent flexDirection='column' gap='20px' width='950px'>
                                        <FlexboxContainerComponent gap='20px'>
                                            <FieldWrapper>
                                                <Input
                                                    id="client-id"
                                                    labelName="Client ID"
                                                    type="text"
                                                    value={showClientId ? (buyNowCredentials?.clientId || '') : getMaskedValue(buyNowCredentials?.clientId || '')}
                                                    disabled
                                                    dirty={false}
                                                    textAlign="left"
                                                    direction="ltr"
                                                />
                                                <FieldActionButtons
                                                    isVisible={showClientId}
                                                    onToggleVisibility={() => setShowClientId(!showClientId)}
                                                    onCopy={() => copyToClipboard(buyNowCredentials?.clientId || '', 'Client ID')}
                                                />
                                            </FieldWrapper>
                                            <FieldWrapper>
                                                <Input
                                                    id="client-secret"
                                                    labelName="Client Secret"
                                                    type="text"
                                                    value={showClientSecret ? (buyNowCredentials?.clientSecret || '') : getMaskedValue(buyNowCredentials?.clientSecret || '')}
                                                    disabled
                                                    dirty={false}
                                                    textAlign="left"
                                                    direction="ltr"
                                                />
                                                <FieldActionButtons
                                                    isVisible={showClientSecret}
                                                    onToggleVisibility={() => setShowClientSecret(!showClientSecret)}
                                                    onCopy={() => copyToClipboard(buyNowCredentials?.clientSecret || '', 'Client Secret')}
                                                />
                                            </FieldWrapper>
                                        </FlexboxContainerComponent>
                                        <FlexboxContainerComponent gap='20px'>
                                            <FieldWrapper>
                                                <Input
                                                    id="client-url"
                                                    labelName="Client URL"
                                                    type="text"
                                                    value={showClientUrl ? (buyNowCredentials?.clientUrl || '') : getMaskedValue(buyNowCredentials?.clientUrl || '')}
                                                    disabled
                                                    dirty={false}
                                                    textAlign="left"
                                                    direction="ltr"
                                                />
                                                <FieldActionButtons
                                                    isVisible={showClientUrl}
                                                    onToggleVisibility={() => setShowClientUrl(!showClientUrl)}
                                                    onCopy={() => copyToClipboard(buyNowCredentials?.clientUrl || '', 'Client URL')}
                                                />
                                            </FieldWrapper>
                                            <FieldWrapper>
                                                <Input
                                                    id="vendor-key"
                                                    labelName="Vendor Key"
                                                    type="text"
                                                    value={showVendorKey ? (buyNowCredentials?.buyNowConsumerKey || '') : getMaskedValue(buyNowCredentials?.buyNowConsumerKey || '')}
                                                    disabled
                                                    dirty={false}
                                                    textAlign="left"
                                                    direction="ltr"
                                                />
                                                <FieldActionButtons
                                                    isVisible={showVendorKey}
                                                    onToggleVisibility={() => setShowVendorKey(!showVendorKey)}
                                                    onCopy={() => copyToClipboard(buyNowCredentials?.buyNowConsumerKey || '', 'Vendor Key')}
                                                />
                                            </FieldWrapper>
                                        </FlexboxContainerComponent>
                                    </FlexboxContainerComponent>
                                </div>
                            </>
                        )}
                    </CheckboxContainer>

                    {showConfigModal && (
                        <BuyNowConfigModal
                            show={showConfigModal}
                            onClose={() => setShowConfigModal(false)}
                            level={API_LEVEL.VENDOR}
                            credentials={buyNowCredentials ? {
                                clientId: buyNowCredentials.clientId,
                                clientSecret: buyNowCredentials.clientSecret,
                                clientUrl: buyNowCredentials.clientUrl,
                                buyNowConsumerKey: buyNowCredentials.buyNowConsumerKey,
                                brandCode: buyNowCredentials.vendorCode,
                            } : null}
                        />
                    )}
                </>
            }

        </FlexboxContainerComponent>
    );
}

export default AutomationSettings;
